import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTaxDeclarationProgress, upsertTaxDeclarationProgress } from '../taxDeclarationApi';
import { fetchRetirementAllowanceCandidates } from './retirementAllowanceApi';
import { retirementAllowanceFromApi } from './retirementAllowanceMapper';
import {
  buildResumeStep,
  entryHasAmount,
  gateQuestion,
  isRetirementAllowanceComplete,
  landOnStepAfterUndo,
  parseRetirementAllowanceCursor,
  retirementAllowanceCursor,
  showUndoForStep,
  stepIndex,
  stepQuestionKey,
  undoTargetStep,
} from './retirementAllowanceSteps';
import { parseIncomeYen, formatIncomeYen } from '../spouseSection/spouseEligibility';
import type {
  FamilyMemberCandidate,
  RetirementAllowanceEntry,
  RetirementAllowanceSnapshot,
  RetirementAllowanceStep,
  RetirementRecipientCategory,
  YesNo,
} from './retirementAllowanceTypes';
import {
  emptyRetirementAllowanceSnapshot,
  RETIREMENT_ALLOWANCE_STEP_ORDER,
} from './retirementAllowanceTypes';

export function useRetirementAllowanceFlow(
  email: string | null,
  taxYear: number,
  t: (key: string) => string,
  active: boolean,
  onSectionDoneChange: (done: boolean) => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);
  const [step, setStep] = useState<RetirementAllowanceStep>('intro');
  const [snap, setSnap] = useState<RetirementAllowanceSnapshot>(emptyRetirementAllowanceSnapshot());
  const [spouse, setSpouse] = useState<FamilyMemberCandidate | null>(null);
  const [dependents, setDependents] = useState<FamilyMemberCandidate[]>([]);
  const [committed, setCommitted] = useState<Set<RetirementAllowanceStep>>(() => new Set());
  const [reviewMode, setReviewMode] = useState(false);
  const [addAnotherAnswer, setAddAnotherAnswer] = useState<YesNo | ''>('');
  const [amountDraft, setAmountDraft] = useState('');
  const [sectionDonePersisted, setSectionDonePersisted] = useState(false);
  const onDoneRef = useRef(onSectionDoneChange);
  onDoneRef.current = onSectionDoneChange;

  const reviewModeActive = reviewMode || step === 'review';
  const isComplete = sectionDonePersisted && step === 'review';

  const usedFamilyIds = useCallback(
    () => new Set(snap.entries.map((e) => e.empFamilyId)),
    [snap.entries]
  );

  const availableDependents = useCallback(() => {
    const used = usedFamilyIds();
    return dependents.filter((c) => !used.has(c.empFamilyId));
  }, [dependents, usedFamilyIds]);

  const markCommitted = useCallback((s: RetirementAllowanceStep) => {
    setCommitted((prev) => new Set(prev).add(s));
  }, []);

  const persist = useCallback(
    async (opts: {
      nextStep?: RetirementAllowanceStep | null;
      snapPatch?: Partial<RetirementAllowanceSnapshot>;
      sectionDone?: boolean;
    }) => {
      if (!email?.trim()) return false;
      const merged = { ...snap, ...(opts.snapPatch ?? {}) };
      setSnap(merged);
      const saved = await upsertTaxDeclarationProgress({
        email: email.trim(),
        taxYear,
        ...(opts.nextStep !== undefined
          ? { currentStepId: retirementAllowanceCursor(opts.nextStep ?? 'review') }
          : {}),
        ...(opts.sectionDone !== undefined ? { sectionOtherIncomeDone: opts.sectionDone } : {}),
        retirementAllowance: merged,
      });
      if (!saved) {
        setSaveError(true);
        return false;
      }
      setSaveError(false);
      if (saved.retirementAllowance) setSnap(retirementAllowanceFromApi(saved.retirementAllowance));
      if (opts.sectionDone === true) {
        setSectionDonePersisted(true);
        onDoneRef.current(true);
      } else if (opts.sectionDone === false) {
        setSectionDonePersisted(false);
        onDoneRef.current(false);
      } else if (saved.sectionOtherIncomeDone === true) {
        setSectionDonePersisted(true);
        onDoneRef.current(true);
      }
      return true;
    },
    [email, snap, taxYear]
  );

  useEffect(() => {
    if (!active || !email?.trim()) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const [progress, cands] = await Promise.all([
        fetchTaxDeclarationProgress(email.trim(), taxYear),
        fetchRetirementAllowanceCandidates(email.trim()),
      ]);
      if (cancelled) return;
      setSpouse(cands.spouse);
      setDependents(cands.dependents);
      const loaded = retirementAllowanceFromApi(progress?.retirementAllowance);
      setSnap(loaded);
      let sectionDone = progress?.sectionOtherIncomeDone === true;
      if (!sectionDone && isRetirementAllowanceComplete(loaded)) {
        const saved = await upsertTaxDeclarationProgress({
          email: email.trim(),
          taxYear,
          currentStepId: retirementAllowanceCursor('review'),
          sectionOtherIncomeDone: true,
          retirementAllowance: loaded,
        });
        if (saved?.sectionOtherIncomeDone === true) {
          sectionDone = true;
          setSectionDonePersisted(true);
          onDoneRef.current(true);
        }
      } else {
        setSectionDonePersisted(sectionDone);
        if (sectionDone) onDoneRef.current(true);
      }
      const cursor = parseRetirementAllowanceCursor(progress?.currentStepId);
      const resume = cursor ?? buildResumeStep(loaded, sectionDone);
      if (sectionDone || isRetirementAllowanceComplete(loaded)) {
        setReviewMode(true);
        setStep('review');
        setCommitted(new Set(['intro', 'gate', 'addAnotherDependent', 'enterAmount'] as RetirementAllowanceStep[]));
        setAddAnotherAnswer(loaded.entries.length > 0 ? 'no' : '');
      } else {
        setReviewMode(false);
        setStep(resume === 'gate' ? 'intro' : resume);
        const comm = new Set<RetirementAllowanceStep>(['intro']);
        if (loaded.anyExpected !== '') comm.add('gate');
        if (loaded.recipientCategory !== '') comm.add('recipientType');
        setCommitted(comm);
        setAddAnotherAnswer('');
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [active, email, taxYear]);

  const skipIntro = useCallback(async () => {
    markCommitted('intro');
    setStep('gate');
    await persist({ nextStep: 'gate' });
  }, [markCommitted, persist]);

  const handleGate = useCallback(
    async (v: YesNo) => {
      const patch: Partial<RetirementAllowanceSnapshot> = {
        anyExpected: v,
        recipientCategory: v === 'no' ? '' : snap.recipientCategory,
        entries: v === 'no' ? [] : snap.entries,
      };
      markCommitted('gate');
      setAddAnotherAnswer('');
      const next = v === 'yes' ? 'recipientType' : 'review';
      setSnap((s) => ({ ...s, ...patch }));
      setStep(next);
      if (v === 'no') {
        setReviewMode(true);
        await persist({ nextStep: 'review', snapPatch: patch, sectionDone: true });
        return;
      }
      setReviewMode(false);
      await persist({ nextStep: next, snapPatch: patch, sectionDone: false });
    },
    [markCommitted, persist, snap.entries, snap.recipientCategory]
  );

  const handleRecipientType = useCallback(
    async (v: RetirementRecipientCategory) => {
      if (v === 'spouse' && !spouse) return;
      if (v === 'dependents' && dependents.length === 0) return;
      const patch: Partial<RetirementAllowanceSnapshot> = {
        recipientCategory: v,
        entries: [],
      };
      markCommitted('recipientType');
      setAddAnotherAnswer('');
      setSnap((s) => ({ ...s, ...patch }));
      const next = v === 'spouse' ? 'pickSpouse' : 'pickDependent';
      setStep(next);
      await persist({ nextStep: next, snapPatch: patch, sectionDone: false });
    },
    [dependents.length, markCommitted, persist, spouse]
  );

  const handlePickSpouse = useCallback(async () => {
    if (!spouse) return;
    const entry: RetirementAllowanceEntry = {
      empFamilyId: spouse.empFamilyId,
      memberFullName: spouse.fullName,
      memberRelationship: spouse.relationship,
      retirementAllowanceAmount: '',
    };
    const patch = { entries: [entry] };
    markCommitted('pickSpouse');
    setAmountDraft('');
    setStep('enterAmount');
    setSnap((s) => ({ ...s, ...patch }));
    await persist({ nextStep: 'enterAmount', snapPatch: patch, sectionDone: false });
  }, [markCommitted, persist, spouse]);

  const handlePickDependent = useCallback(
    async (c: FamilyMemberCandidate) => {
      const entry: RetirementAllowanceEntry = {
        empFamilyId: c.empFamilyId,
        memberFullName: c.fullName,
        memberRelationship: c.relationship,
        retirementAllowanceAmount: '',
      };
      const entries = [...snap.entries, entry];
      const patch = { entries };
      markCommitted('pickDependent');
      setAmountDraft('');
      setStep('enterAmount');
      setSnap((s) => ({ ...s, ...patch }));
      await persist({ nextStep: 'enterAmount', snapPatch: patch, sectionDone: false });
    },
    [markCommitted, persist, snap.entries]
  );

  const handleEnterAmount = useCallback(async () => {
    const amount = amountDraft.trim();
    if (parseIncomeYen(amount) <= 0 || snap.entries.length === 0) return;
    const entries = snap.entries.map((e, i) =>
      i === snap.entries.length - 1 ? { ...e, retirementAllowanceAmount: amount } : e
    );
    const patch = { entries };
    markCommitted('enterAmount');
    setSnap((s) => ({ ...s, ...patch }));
    if (snap.recipientCategory === 'spouse') {
      setReviewMode(true);
      setStep('review');
      await persist({ nextStep: 'review', snapPatch: patch, sectionDone: true });
      return;
    }
    setStep('addAnotherDependent');
    await persist({ nextStep: 'addAnotherDependent', snapPatch: patch, sectionDone: false });
  }, [amountDraft, markCommitted, persist, snap.entries, snap.recipientCategory]);

  const handleAddAnotherDependent = useCallback(
    async (v: YesNo) => {
      setAddAnotherAnswer(v);
      if (v === 'yes') {
        markCommitted('addAnotherDependent');
        setStep('pickDependent');
        await persist({ nextStep: 'pickDependent' });
        return;
      }
      markCommitted('addAnotherDependent');
      setReviewMode(true);
      setStep('review');
      await persist({ nextStep: 'review', sectionDone: true });
    },
    [markCommitted, persist]
  );

  const enterReviewFromMenu = useCallback(() => {
    setReviewMode(true);
    setStep('review');
    setCommitted(new Set(['intro', 'gate', 'addAnotherDependent'] as RetirementAllowanceStep[]));
    setAddAnotherAnswer(snap.entries.length > 0 ? 'no' : '');
  }, [snap.entries.length]);

  const ensureSectionPersisted = useCallback(async (): Promise<boolean> => {
    if (sectionDonePersisted) return true;
    if (!isRetirementAllowanceComplete(snap)) return false;
    setReviewMode(true);
    setStep('review');
    return persist({ nextStep: 'review', sectionDone: true });
  }, [persist, sectionDonePersisted, snap]);

  const handleUndo = useCallback(
    async (undoFromStep?: RetirementAllowanceStep) => {
      onDoneRef.current(false);
      setReviewMode(false);

      const undoFrom =
        undoFromStep ??
        undoTargetStep(step, snap, committed, reviewModeActive) ??
        (step === 'review' && snap.entries.length > 0
          ? ('addAnotherDependent' as RetirementAllowanceStep)
          : step === 'review'
            ? ('gate' as RetirementAllowanceStep)
            : null);
      if (!undoFrom) return;

      if (undoFrom === 'gate') {
        const patch: Partial<RetirementAllowanceSnapshot> = {
          anyExpected: '',
          recipientCategory: '',
          entries: [],
        };
        setSnap((s) => ({ ...s, ...patch }));
        setStep('gate');
        setCommitted(new Set(['intro']));
        setAddAnotherAnswer('');
        await persist({ nextStep: 'gate', snapPatch: patch, sectionDone: false });
        return;
      }

      if (undoFrom === 'enterAmount' && snap.entries.length > 0) {
        const last = snap.entries[snap.entries.length - 1];
        setAmountDraft(last.retirementAllowanceAmount ?? '');
        setStep('enterAmount');
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('enterAmount');
          return next;
        });
        await persist({ nextStep: 'enterAmount', sectionDone: false });
        return;
      }

      if (undoFrom === 'addAnotherDependent' && snap.entries.length > 0) {
        const entries = snap.entries.slice(0, -1);
        const patch = { entries };
        setSnap((s) => ({ ...s, ...patch }));
        setStep('addAnotherDependent');
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('addAnotherDependent');
          return next;
        });
        setAddAnotherAnswer('');
        await persist({ nextStep: 'addAnotherDependent', snapPatch: patch, sectionDone: false });
        return;
      }

      if (undoFrom === 'recipientType') {
        const patch: Partial<RetirementAllowanceSnapshot> = {
          recipientCategory: '',
          entries: [],
        };
        setSnap((s) => ({ ...s, ...patch }));
        setStep('recipientType');
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('recipientType');
          next.delete('pickSpouse');
          next.delete('pickDependent');
          next.delete('addAnotherDependent');
          return next;
        });
        setAddAnotherAnswer('');
        await persist({ nextStep: 'recipientType', snapPatch: patch, sectionDone: false });
        return;
      }

      if (undoFrom === 'pickSpouse' || undoFrom === 'pickDependent') {
        const patch = { entries: [] as RetirementAllowanceEntry[] };
        setSnap((s) => ({ ...s, ...patch }));
        setAmountDraft('');
        setStep(undoFrom);
        setCommitted((prev) => {
          const next = new Set(prev);
          next.delete('pickSpouse');
          next.delete('pickDependent');
          next.delete('enterAmount');
          next.delete('addAnotherDependent');
          return next;
        });
        setAddAnotherAnswer('');
        await persist({ nextStep: undoFrom, snapPatch: patch, sectionDone: false });
        return;
      }

      const landOn = landOnStepAfterUndo(undoFrom, snap);
      setStep(landOn);
      setCommitted((prev) => {
        const next = new Set(prev);
        for (const s of RETIREMENT_ALLOWANCE_STEP_ORDER) {
          if (stepIndex(s) >= stepIndex(landOn)) next.delete(s);
        }
        return next;
      });
      await persist({ nextStep: landOn, sectionDone: false });
    },
    [committed, persist, reviewModeActive, snap, step]
  );

  const canShowUndoForStep = useCallback(
    (s: RetirementAllowanceStep) =>
      showUndoForStep(s, step, snap, committed.has(s), reviewModeActive, committed),
    [committed, reviewModeActive, snap, step]
  );

  const userAnswerForStep = useCallback(
    (s: RetirementAllowanceStep, entry?: RetirementAllowanceEntry): string => {
      switch (s) {
        case 'gate':
          return t(
            snap.anyExpected === 'yes'
              ? 'submitDocuments.dependentDeduction.retirementAllowance.yes'
              : 'submitDocuments.dependentDeduction.retirementAllowance.no'
          );
        case 'recipientType':
          return snap.recipientCategory === 'spouse'
            ? t('submitDocuments.dependentDeduction.retirementAllowance.recipient.spouse')
            : t('submitDocuments.dependentDeduction.retirementAllowance.recipient.dependents');
        case 'pickSpouse':
        case 'pickDependent':
          return entry
            ? `${entry.memberFullName} (${t(`submitDocuments.familyInfo.relationship.${entry.memberRelationship}`)})`
            : '';
        case 'enterAmount':
          return entry && entryHasAmount(entry)
            ? `${formatIncomeYen(parseIncomeYen(entry.retirementAllowanceAmount))} ${t('submitDocuments.dependentDeduction.retirementAllowance.yenSuffix')}`
            : amountDraft.trim()
              ? `${formatIncomeYen(parseIncomeYen(amountDraft))} ${t('submitDocuments.dependentDeduction.retirementAllowance.yenSuffix')}`
              : '';
        case 'addAnotherDependent':
          return addAnotherAnswer === 'yes'
            ? t('submitDocuments.dependentDeduction.retirementAllowance.yes')
            : addAnotherAnswer === 'no'
              ? t('submitDocuments.dependentDeduction.retirementAllowance.no')
              : '';
        default:
          return '';
      }
    },
    [addAnotherAnswer, amountDraft, snap.anyExpected, snap.recipientCategory, t]
  );

  const canSendAmount = parseIncomeYen(amountDraft) > 0;

  return {
    t,
    taxYear,
    step,
    snap,
    spouse,
    dependents,
    availableDependents,
    committed,
    isLoading,
    saveError,
    reviewMode: reviewModeActive,
    isComplete,
    skipIntro,
    handleGate,
    handleRecipientType,
    handlePickSpouse,
    handlePickDependent,
    handleEnterAmount,
    handleAddAnotherDependent,
    amountDraft,
    setAmountDraft,
    canSendAmount,
    enterReviewFromMenu,
    ensureSectionPersisted,
    handleUndo,
    canShowUndoForStep,
    undoLabel: t('submitDocuments.chat.undo'),
    userAnswerForStep,
    entryHasAmount,
    stepQuestionKey: (s: RetirementAllowanceStep) => stepQuestionKey(s, taxYear),
    gateQuestion: () => gateQuestion(t, taxYear),
    isCommitted: (s: RetirementAllowanceStep) => committed.has(s),
  };
}

export type RetirementAllowanceFlow = ReturnType<typeof useRetirementAllowanceFlow>;

export { isRetirementAllowanceCursor } from './retirementAllowanceSteps';
